import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-final-form';
import type { IDropdown } from '@alfa-bank/site-form-utils/src/components/base-select/models';
import type { TChangeEvent } from '@alfa-bank/site-pure-input/src/models';
import throttle from 'lodash/throttle';
import { sendEvent } from '@alfa-bank/site-helpers-common/browser/send-event';

import { dataFieldName, getSuggestionAddress } from '../../helpers';
import type { ISuggestions } from '../../models';
import { deliveryStepAnalytics as analytics } from '../../../../analytics';

import type { IAddressInputProps } from './models';

type TUseAddressField = Pick<
    IAddressInputProps,
    'dadataDelay' | 'name' | 'onCancel' | 'onChange' | 'onSelect' | 'onFocus' | 'value'
>;

export const useAddressField = ({
    dadataDelay,
    name,
    onCancel,
    onChange,
    onFocus,
    onSelect,
    value,
}: TUseAddressField) => {
    const formApi = useForm();
    const addressObj = formApi.getState().values[dataFieldName(name)];

    const [suggestions, setSuggestions] = useState<ISuggestions[]>([]);
    const setAddress = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (selected: any) => {
            if (!selected) return;

            formApi.change(dataFieldName(name), selected.data);
            // TODO: посмотреть где оставить тут или выше
            // dispatch(updateClientAddress(selected.value));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [formApi, name]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const throttledOnSelectRequest = useCallback(
        throttle(
            valueString => {
                getSuggestionAddress(valueString).then(setSuggestions).catch(console.error);
            },
            dadataDelay,
            { leading: false }
        ),
        [getSuggestionAddress, dadataDelay]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const throttledOnChangeRequest = useCallback(
        throttle(
            valueString => {
                getSuggestionAddress(valueString)
                    .then(suggestionsArray => {
                        setSuggestions(suggestionsArray);

                        formApi.change(`${name}-isSuggestions`, Boolean(suggestionsArray.length));
                    })
                    .catch(console.error);
            },
            dadataDelay,
            { leading: false }
        ),
        [getSuggestionAddress, dadataDelay]
    );

    const handleSelect = useCallback(
        (selected: IDropdown) => {
            const valueString = selected.value.toString();

            setAddress(selected);
            onSelect(valueString);

            throttledOnSelectRequest(valueString);

            const { errors } = formApi.getState();

            if (errors.deliveryAddress) {
                sendEvent(analytics.formFields.deliveryAddress.incorrect);
            } else {
                sendEvent(analytics.formFields.deliveryAddress.correct);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setAddress, onSelect, throttledOnSelectRequest]
    );

    const handleFocus = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (event: any) => {
            onFocus(event);
            sendEvent(analytics.formFields.deliveryAddress.focus);
        },
        [onFocus]
    );

    const handleChange = useCallback(
        ({ value: val }: TChangeEvent) => {
            const valueString = val.toString();

            onChange(valueString);

            if (valueString.length === 0) {
                setSuggestions([]);

                return;
            }

            if (valueString.length < 3) return;

            throttledOnChangeRequest(valueString);
        },
        [onChange, throttledOnChangeRequest]
    );

    const handleCancel = useCallback(() => {
        onCancel();
        onChange('');
    }, [onChange, onCancel]);

    useEffect(() => {
        if (value !== addressObj?.fullName) {
            setAddress(null);
        }
    }, [addressObj?.fullName, formApi, value, name, setAddress]);

    return {
        suggestions,
        handleChange,
        handleSelect,
        handleCancel,
        handleFocus,
    };
};
